import React from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import useResponsive from "../Hooks/responsive";

export default function Terms() {
  const isMobile = useResponsive();
  return (
    <div className="home-page">
      <Navbar />
      <div className="terms-page">
        <h1 className="cFMxiM">Terms &amp; Conditions</h1>
        <div
          itemprop="text"
          font-family="1"
          className="styles__StyledDiv-sc-jb2at6-0 gGDurp"
          style={isMobile ? { width: "90vw", overflowX: "hidden" } : {}}
        >
          <p className="medium" style={{ marginBottom: 32 }}>
            Effective 20 June, 2023
          </p>
          <p className="medium">
            CelebriLinks is a content sharing platform that enables creators to
            connect their audiences with their most recent and relevant content.
            One of our core values is to pursue deliberate simplicity — so we’ve
            tried to distill our terms and conditions into their simplest form
            here.
          </p>
          <h5>Welcome to CelebriLinks!</h5>
          <p>
            It’s great to have you here. By using CelebriLinks, you agree to
            these Terms &amp; Conditions (“Terms”) and the other policies that
            we link to here and on the Platform.
          </p>
          <p className="medium">
            These Terms (and the policies we link to) apply to your use of our
            website (<a href="https://linktr.ee/">https://linktr.ee/</a>), apps
            and any other software or features provided in connection with our
            services (collectively the “Platform” or “CelebriLinks”). When we
            say “we”, “our” or “us” in these Terms, we’re referring to
            CelebriLinks Pty Ltd (ABN 68 608 721 562) and our group companies.
          </p>
          <p className="medium">
            Please read these Terms carefully and reach out if you have any
            questions. If you don’t agree with them, please don’t use
            CelebriLinks.
          </p>
          <h5>Your account</h5>
          <p>
            To create an account and become a CelebriLinks user, you must be at
            least 18. If you’re creating an account on behalf of someone else,
            you must have their permission to do so. You’re responsible for your
            account and using it in a lawful way.
          </p>
          <p className="medium">
            When you create an account, you agree to comply with these Terms and
            that you’re (i) over 18, and (ii) legally able to enter these Terms
            with us. You must provide us with accurate info about yourself — if
            anything changes, please let us know so we can update your details.
          </p>
          <p className="medium">
            If you’re using CelebriLinks on behalf of a business or individual,
            you agree that you’re authorised by them to act and agree to these
            Terms on their behalf.
          </p>
          <p className="medium">
            You’re responsible for anything that happens to your account, so
            keep your login details and password safe and don’t share them with
            anyone. If you think your account has been compromised, please
            contact us immediately.
          </p>
          <p className="medium">
            You must not assign or transfer your account to someone else, or use
            your account (or allow it to be used by anyone) in a way which in
            our reasonable opinion, causes damage to CelebriLinks or our
            reputation, or infringes another’s rights or applicable laws and
            regulations.
          </p>
          <h5 style={{ fontWeight: "500" }}>Your username</h5>
          <p style={{ fontSize: "clamp(20px, 2vmin, 20px)" }}>
            The username you choose must be appropriate for everyone and cannot
            infringe someone else’s rights.
          </p>
          <p className="medium">
            We know the username you choose is very important to you. But it’s
            also important that your username is appropriate for all audiences
            and doesn’t infringe anyone’s rights, including intellectual
            property rights (such as copyright and trade marks) (“IP Rights”).
          </p>
          <p className="medium">
            You can’t use the name of another person (such as a celebrity),
            brand or company, have a username that is offensive, vulgar or
            obscene, or create an account with a username that you have no
            connection with just to profit from it in the future (known as
            “Domain Squatting”).
          </p>
          <p className="medium">
            If any of these issues occur over your username, we’ll consider the
            circumstances reasonably and may require you to change it (and we
            may reassign it to someone else). If you refuse, we may suspend or
            cancel your account. If someone claims that your username infringes
            their IP Rights, they’ll need to complete an&nbsp;
            <a href="https://linktr.ee/s/report-ip-infringements/">
              Intellectual Property report
            </a>
            &nbsp;and you’ll have the chance to then issue a Counter Notice.
          </p>
          <p className="medium">
            If you’ve not signed in or added new links or had any traffic to
            your account in the last 6 months we may reclaim or reassign your
            username (but never without contacting you first).
          </p>
          <h5>Your content — what is and isn’t allowed</h5>
          <p>
            We love the variety of content that our users post on CelebriLinks!
            However, we want to ensure that everyone who visits the Platform can
            do so safely. That’s why we have our&nbsp;
            <a href="https://linktr.ee/s/community-standards/">
              Community Standards
            </a>
            .
          </p>
          <p className="medium">
            Our&nbsp;
            <a href="https://linktr.ee/s/community-standards/">
              Community Standards&nbsp;
            </a>
            set out what content is and isn’t allowed on CelebriLinks, so please
            make sure you follow them, otherwise we may suspend or permanently
            remove your account.
          </p>
          <p className="medium">
            When we talk about your “content”, we mean the text, graphics,
            videos, links, and any other materials you add to your CelebriLinks
            profile. You’re responsible for your content and you warrant that:
          </p>
          <ul>
            <li>
              <p className="medium">
                it’s yours or if you’re using third party content, you have all
                of the rights needed to post it on CelebriLinks (and to let us
                use it in accordance with these Terms);
              </p>
            </li>
            <li>
              <p className="medium">
                none of your content will violate the privacy, publicity, IP or
                other rights of anyone else;
              </p>
            </li>
            <li>
              <p className="medium">
                your content will not (i) be misleading or deceptive, intended
                or designed to misinform, or likely to misinform a reasonable
                person, (ii) cause either of us to break any laws or legal
                obligations, and (iii) bring us into disrepute or damage our
                reputation;
              </p>
            </li>
            <li>
              <p className="medium">
                your content doesn’t contain any viruses or other harmful code,
                files, or programs designed to interrupt or damage the
                functionality of the Platform or any other software, hardware or
                device;
              </p>
            </li>
            <li>
              <p className="medium">
                you will not use, or authorise others to use, automated scripts
                or other scraping tools to collect information from your
                profile, or the Platform;
              </p>
            </li>
            <li>
              <p className="medium">
                you won’t post any unauthorised advertising, solicitations or
                endorsements on CelebriLinks; and
              </p>
            </li>
            <li>
              <p className="medium">
                all of your content complies with the&nbsp;
                <a href="https://linktr.ee/s/community-standards/">
                  Community Standards
                </a>
                .
              </p>
            </li>
          </ul>
          <p className="medium">
            Since laws and regulations differ from country to country, we may
            ban content that might be legal in some countries, and to take
            whatever action we think necessary, including removing content or
            restricting access to it or the Platform.
          </p>
          <h5>Your content — what we can do with it</h5>
          <p>
            We love your content and want to show it off. When you post content
            on CelebriLinks, you give us the right to display, use, and share
            it.
          </p>
          <p className="medium">
            When you post content on your profile, you grant us a licence to (i)
            use, publicly display, distribute, modify, adapt and create
            derivative works of such content; and (ii) use your name, image,
            voice, photograph, likeness and any other personal attributes in the
            content; on the Platform and in our marketing in all media (such as
            our social channels and any other advertising). This licence is
            worldwide, royalty-free and perpetual, which means we can use your
            content anywhere in the world, without paying you fees, for as long
            as we like. You agree that you have all third party rights necessary
            to post the content on CelebriLinks and to grant us this licence.
          </p>
          <p className="medium">
            You’ll retain all of your rights in your content. But keep in mind
            that your content will be publicly accessible, and may be used and
            re-shared by others on CelebriLinks and across the internet.
          </p>
          <p className="medium">
            Please don’t share personal info that you don’t want visible to the
            world, and never post social security numbers, passport details or
            similar info that could cause harm in the wrong hands. You may only
            post personal info relating to others where you have their consent
            and have kept a record of it.
          </p>
          <p className="medium">
            We don’t have to monitor the accuracy, reliability or legality of
            your content, but we may choose to do so. We may modify, remove or
            restrict access to content at any time in line with these Terms or
            apply a sensitive content warning to content that we deem unsuitable
            for all audiences.
          </p>
          <h5>Our Platform</h5>
          <p>
            We own the Platform, but give you a limited right to use it for
            sharing content and viewing and interacting with other users’
            content. We’re not responsible for any content, products or services
            made available via other users’ profiles.
          </p>
          <p className="medium">
            All rights (including IP Rights) in and to the Platform (excluding
            your content) (the “CelebriLinks IP”) are and will remain
            exclusively owned by CelebriLinks or our licensors. You won’t
            acquire any rights in the CelebriLinks IP and you’re not allowed to
            use it (including our brand name or logo) for any purpose (such as
            to imply a partnership with, or endorsement from CelebriLinks),
            without our prior written approval.
          </p>
          <p className="medium">
            As a user, we grant you a limited, revocable, non-exclusive,
            non-transferable right to use the Platform to create, display, use,
            play, and upload content in accordance with these Terms. If we
            provide you with any images, icons, themes, fonts, videos, graphics,
            or other content, you must only use them on your profile and comply
            with any guidelines we make available to you.
          </p>
          <p className="medium">
            You must not remove, obscure or alter any proprietary notices or
            trade marks on the Platform, or make unauthorised copies of,
            reproduce, distribute, licence, sell, resell, modify, translate,
            disassemble, decompile, decrypt, reverse engineer, create any
            derivative works from, or attempt to derive the source code of, the
            Platform or any part of it. If you visit CelebriLinks profiles (a
            “profile visitor”), we grant you a limited, non-exclusive,
            non-transferable right to view and interact with the Platform via
            user profiles.
          </p>
          <p className="medium">
            To the extent allowed by law, we’re not responsible for any
            opinions, advice, statements, products, services, offers or other
            content posted by other users on their profiles.
          </p>
          <h5>Your plan</h5>
          <p>
            You can sign up to CelebriLinks on a free or paid plan and cancel at
            any time.
          </p>
          <p className="medium">
            Your plan will start when you accept these Terms and continue until
            you cancel it. If you cancel a paid plan it will ordinarily continue
            until the end of your current billing cycle and then automatically
            convert to a free plan.
          </p>
          <p className="medium">
            To cancel, visit the billing page (
            <a href="https://linktr.ee/admin/billing">
              https://linktr.ee/admin/billing
            </a>
            ). To the extent permitted by applicable law, payments are
            non-refundable. But we know that sometimes your requirements may
            change. So, if you’ve selected a paid plan, but cancel within 72
            hours, we may make an exception (please email us at&nbsp;
            <a>support@linktr.ee</a>).
          </p>
          <p className="medium">
            However, if you downloaded our app and purchased your plan using
            your Apple ID or Google Play Store account, you’ll need to cancel by
            following the instructions provided by:
          </p>
          <ul>
            <li>
              <p className="medium">
                Apple at&nbsp;
                <a href="https://support.apple.com/en-au/HT202039">
                  https://support.apple.com/en-au/HT202039
                </a>
                ; or
              </p>
            </li>
            <li>
              <p className="medium">
                Google at&nbsp;
                <a href="https://support.google.com/googleplay/answer/7018481">
                  https://support.google.com/googleplay/answer/7018481
                </a>
                .
              </p>
            </li>
          </ul>
          <p className="medium">
            Please note that CelebriLinks is unable to process refunds in these
            circumstances. You can request a refund from Apple on the&nbsp;
            <a href="https://getsupport.apple.com/">Apple Support</a>
            &nbsp;webpage or Google by following&nbsp;
            <a href="https://support.google.com/googleplay/answer/7205930">
              these instructions
            </a>
            .
          </p>
          <p className="medium">
            If you have cancelled your plan and would like to permanently delete
            your account, you can do so on your ‘
            <a href="https://linktr.ee/admin/account/">My Account</a>’ page.
            Remember, if you delete your account, you won’t be able to
            reactivate it or retrieve any of the content or info you have added
            to your profile.
          </p>
          <h5>Our fees</h5>
          <p>
            You must pay all subscription fees when they become due.
            CelebriLinks transaction fees as well as processing fees will be
            deducted automatically from the proceeds of any transactions you
            make on your profile.
          </p>
          <ul>
            <li>
              <p className="medium">
                Subscription fees —&nbsp;If you’ve chosen a paid plan, you must
                pay your subscription fees when they fall due. Unless otherwise
                stated, fees are in USD and are inclusive of transactional taxes
                (e.g. GST/VAT).
              </p>
            </li>
          </ul>
          <p className="medium">
            All subscription fees will be billed in advance on a recurring
            basis, based on your chosen billing cycle (either monthly or
            annual). Your paid plan will automatically renew under these Terms
            (as updated at the time of the renewal) at the end of your billing
            cycle unless you’ve cancelled your plan.
          </p>
          <p className="medium">
            You must provide a valid payment method (i.e. credit card) and
            accurate billing information when you join. In doing so, you
            authorise us to charge all fees to such payment method. If automatic
            billing fails, we’ll issue you an online invoice, which you must pay
            within the time indicated.
          </p>
          <ul>
            <li>
              <p className="medium">
                CelebriLinks transaction fees —&nbsp;We may charge a percentage
                of all transactions made on your profile (e.g. through our
                “Commerce” or “Support Me” features). These transaction fees
                will differ depending on your plan and will be set out on
                our&nbsp;<a href="https://linktr.ee/s/pricing/">pricing page</a>
                .
              </p>
            </li>
          </ul>
          <p className="medium">
            You agree that if we charge transaction fees, they will be deducted
            from the proceeds of your transaction prior to receipt. You’re
            responsible for paying any external fees and taxes (including
            withholding taxes) if they apply, and income taxes, associated with
            payments you receive through the Platform.
          </p>
          <ul>
            <li>
              <p className="medium">
                Processing fees —&nbsp;We use third party payment processing
                services (including PayPal, Stripe and Square) to power your
                transactions on CelebriLinks. These services are subject to a
                processing fee. Each payment processor charges its own
                processing fees in accordance with its terms and conditions,
                which you are responsible for reviewing, accepting and complying
                with.
              </p>
            </li>
          </ul>
          <p className="medium">
            You agree that processing fees will be deducted from the proceeds of
            your transaction prior to receipt and that CelebriLinks is not
            responsible for these fees.
          </p>
          <p className="medium">
            We may change our subscription or transaction fees from time to
            time, but we’ll always give you prior notice (and we will aim to do
            so at least 1 month in advance). If you don’t agree to the revised
            fees, you can cancel your plan before your next billing cycle
            starts.
          </p>
          <h5>Privacy &amp; data</h5>
          <p>We care about the privacy of our users and Profile Visitors.</p>
          <p className="medium">
            Our&nbsp;<a href="https://linktr.ee/s/privacy/">Privacy Notice</a>
            &nbsp;explains how we handle your personal data for our own
            purposes. Whereas our&nbsp;
            <a href="https://linktr.ee/s/data-processing-addendum">
              Data Processing Addendum
            </a>
            &nbsp;(“DPA”) (which is incorporated into these Terms) sets out your
            responsibilities (and ours) for respecting the privacy rights of
            visitors to your profile. In creating your account, you agree that
            where the DPA applies to you, you have read, understood and agreed
            to it. To find out how we use cookies, visit our&nbsp;
            <a href="https://linktr.ee/s/cookie-notice/">Cookie Notice</a>.
          </p>
          <p className="medium">
            All data (and any IP rights in data) that we or the Platform create
            or generate based on your use (or profile visitors’, or other users’
            use) of the Platform or content (“Data”) will be owned by us. We may
            provide Data or visualisations of Data to you as part of the service
            we offer on the Platform (we call this “Data Analytics”). We make no
            representations or warranties as to the accuracy or completeness of
            the Data Analytics, but we’ll try to make it as accurate and
            complete as we can.
          </p>
          <h5>Your visitors &amp; customers</h5>
          <p>
            You’re responsible for your profile visitors, including customers
            who purchase goods or services through your CelebriLinks
            (collectively “End Users”).
          </p>
          <p className="medium">
            You’re solely responsible for (i) the way in which End Users use and
            interact with your profile and your content; (ii) complying with all
            laws in relation to your End Users, and all transactions between you
            and End Users made through your CelebriLinks (e.g. via our
            “Commerce” or “Payment lock” features).
          </p>
          <p className="medium">
            You also agree that any donations made to you via our “Support Me”
            feature are made voluntarily, without exchange for any good or
            service. You must use this feature solely to collect donations for
            yourself (and not to collect funds for charities or other causes).
          </p>
          <h5>Confidentiality</h5>
          <p>
            If we share any confidential information about us or the Platform
            with you, you must keep it secret and secure.
          </p>
          <p className="medium">
            From time to time, we may share info with you that is confidential
            (e.g. we may reveal new and upcoming features to you if you take
            part in beta testing with us). You must keep this info secret and
            secure and use reasonable measures to prevent others from accessing
            it.
          </p>
          <h5>Feedback</h5>
          <p>
            We love hearing your ideas on how we can make CelebriLinks even
            better! Sometimes, we may make “beta” functionality available to you
            and seek your feedback.
          </p>
          <p className="medium">
            Keep in mind that if you share feedback with us, we’re free to use
            it however we like, without payment to you (or to not use it at
            all). We may from time to time make certain functionality of the
            Platform available to you in “beta” (or similar). You acknowledge
            that we are still evaluating and testing such beta functionality and
            it may not be as reliable as other parts of the Platform.
          </p>
          <h5>Open Source</h5>
          <p>The app contains open source software.</p>
          <p className="medium">
            The app contains open source software. Certain open source software
            components are subject to their own applicable licence terms, which
            are available in our Open-Source Content library (accessible under
            the ‘Legal’ section in the app). To the extent that any such licence
            terms do not permit the relevant open source component to be
            licensed to you under these Terms, these Terms will not apply to
            that component, and your rights and obligations in respect of that
            component will be governed by the applicable licence terms.
          </p>
          <h5>Liability</h5>
          <p>
            We are not liable for your use of the Platform and you’re
            responsible for keeping backups of your own content.
          </p>
          <ul>
            <li>
              <p className="medium">
                We are not liable for any damages suffered as a result of
                downloading, installing, or using the Platform, or copying,
                distributing, or downloading any content from the Platform. You
                have sole responsibility for the adequate protection and backup
                of your data, content, and devices used in connection with the
                Platform.
              </p>
            </li>
            <li>
              <p className="medium">
                You will indemnify us against any loss arising out of a breach
                by you of these Terms, or a third party claim made against us in
                relation to your content.
              </p>
            </li>
            <li>
              <p className="medium">
                Neither of us will be liable for any indirect, punitive,
                special, incidental or consequential damage (including loss of
                business, revenue, profits, use, privacy, data, goodwill or
                other economic advantage), whether for breach of contract, in
                tort (including negligence) or otherwise, even if we know such
                damage is possible.
              </p>
            </li>
            <li>
              <p className="medium">
                Our liability to you under or in connection with these Terms or
                the Platform will not exceed the greater of the fees you paid to
                us at the time the liability arose, or $100.
              </p>
            </li>
          </ul>
          <h5>Disclaimer</h5>
          <p>There’s a few things we disclaim under these Terms.</p>
          <p className="medium">
            Your use of CelebriLinks and any content contained on the Platform
            is at your sole risk. The Platform is provided on an “AS IS” and “AS
            AVAILABLE” basis, without warranties of any kind, whether express or
            implied, including (but not limited to), up-time or availability, or
            any implied warranties of merchantability, fitness for a particular
            purpose, non-infringement or course of performance.
          </p>
          <p className="medium">
            CelebriLinks, its affiliates and its licensors do not make any
            express or implied warranties or representations, including that:
          </p>
          <ul>
            <li>
              <p className="medium">
                the Platform will function uninterrupted, securely or be
                available at any time or place;
              </p>
            </li>
            <li>
              <p className="medium">any errors or defects will be corrected;</p>
            </li>
            <li>
              <p className="medium">
                the Platform is free of viruses or other harmful components;
              </p>
            </li>
            <li>
              <p className="medium">
                the Platform is effective or the results of using the Platform
                will meet your needs; or
              </p>
            </li>
            <li>
              <p className="medium">
                any content on the Platform (including any user content) is
                complete, accurate, reliable, suitable or available for any
                purpose.
              </p>
            </li>
          </ul>
          <p className="medium">
            These Terms apply to the maximum extent permitted by law and nothing
            in them is intended to exclude, restrict or modify legal rights that
            you may have, which cannot be excluded, restricted or modified by
            contract. If these Terms are governed by the Australian Consumer
            Law, our liability to you for not complying with a consumer
            guarantee is limited to re-supply of the services or payment.
          </p>
          <h5>Third Party Services</h5>
          <p>
            We may include in the Platform, access to specific functionality or
            services which are provided by third parties (e.g. a payment portal
            or online store front).
          </p>
          <p className="medium">
            CelebriLinks integrates with various third party products and
            services. Unless we say otherwise, we do not endorse (or make any
            warranties or representations in relation to) any third party
            product or service and do not offer refunds for any amounts you’ve
            paid to a third party.
          </p>
          <p className="medium">
            Your use of any third party product or service may be subject to
            separate terms and conditions, which you’re responsible for
            reviewing, accepting and complying with. If you don’t accept or
            comply with those third party terms, we may suspend or cancel your
            account, or limit your access to such services within our Platform.{" "}
            <strong>
              If we are obliged to display any specific third party terms on our
              website, those can be found at the foot of this page. We also have
              a list of our subprocessors on our{" "}
              <a href="https://linktr.ee/s/privacy/">Privacy Notice</a>
            </strong>
            .
          </p>
          <h5>App terms</h5>
          <p>
            Using our app involves downloading and installing software to your
            mobile or tablet device. We may automatically update that software
            and these Terms will apply to any updates.
          </p>
          <p className="medium">
            The terms in this section apply where you download and use our app.
            We grant you a limited, revocable, non-exclusive, non-transferable,
            worldwide right to download and install the app on 1 device, which
            you own or control. You must not distribute or make the app
            available over a network where it could be used by many devices at
            once. Where we or you cancel your account, you must delete the app
            from your device.
          </p>
          <p className="medium">
            Where you download the app from the Apple or Google Play stores
            (“App Stores”) you also agree that:
          </p>
          <ul>
            <li>
              <p className="medium">
                these Terms are made between you and CelebriLinks (and not the
                App Store). However, if any of these Terms is inconsistent with
                the App Store terms, the applicable App Store term applies;
              </p>
            </li>
            <li>
              <p className="medium">
                we are solely responsible for the app and its content, any
                maintenance and support services, product warranties (if we
                haven’t disclaimed them), product claims and third party claims
                that the app or your use or possession of it, infringes
                another’s IP Rights. If you have a complaint about the app, or
                become aware of any third party claim, please email us at&nbsp;
                <a>support@linktr.ee</a>&nbsp;(and not the App Store);
              </p>
            </li>
            <li>
              <p className="medium">
                you’re not on a US government restricted parties’ list and you
                don’t live in a country which is subject to a US government
                embargo or designated as “terrorist supporting country”; and
              </p>
            </li>
            <li>
              <p className="medium">
                the App Store and its subsidiaries are third party beneficiaries
                of these Terms, and will have the right to enforce these Terms
                against you as such a beneficiary.
              </p>
            </li>
          </ul>
          <h5>Cancelling your account</h5>
          <h5>
            If you don’t comply with these Terms, we may suspend or cancel your
            account or limit the functionality of the Platform, which you have
            access to (e.g. if you don’t pay your fees on time, we may convert
            your paid plan to a free plan with less features).
          </h5>
          <p className="medium">
            Depending on what the non-compliance is, we may not use this right
            to suspend or cancel your account, but if there is repeat
            non-compliance, or a material non-compliance, we are likely to. If
            we suspend or cancel your account, we will usually notify you
            beforehand (but we don’t have to) and you will not receive a refund
            of any fees paid upfront.
          </p>
          <p className="medium">
            If you think your account has been cancelled by mistake or you have
            an issue with these Terms or the Platform, please email&nbsp;
            <a>support@linktr.ee</a>. Both us and you will use all reasonable
            efforts to resolve the issue in good faith, and neither of us will
            bring any legal action on the issue until we have spent at least 1
            month trying to work it out.
          </p>
          <h5>Changes</h5>
          <p>
            CelebriLinks is constantly evolving and improving. From time to time
            we may make changes to the Platform or these Terms. If the change
            will have a significant negative impact on you, we’ll notify you
            beforehand.
          </p>
          <p className="medium">
            We may need to change these Terms from time-to-time to reflect
            business updates, changes to the Platform (including if we decide to
            discontinue any functionality, features or part of the Platform),
            legal or commercial reasons, or otherwise to protect our legitimate
            interests.
          </p>
          <p className="medium">
            We can make these changes at any time and it’s your responsibility
            to check these Terms now and then for any changes. However, if a
            change will have a material negative effect on you, we’ll use our
            best efforts to notify you at least 1 month before the change takes
            effect (e.g. via a notification on the Platform).
          </p>
          <p className="medium">
            Your continued use of the Platform after any changes to the Terms is
            taken as your acceptance of the revised Terms. If you don’t agree
            with the changes, we ask you to stop using CelebriLinks and cancel
            your account.
          </p>
          <h5>A few other things</h5>
          <p>
            There’s a few more things we need to say before you can use
            CelebriLinks.
          </p>
          <p className="medium">
            Laws may apply to you or us as a result of your specific location.
            If this is the case, and if those laws are inconsistent with any
            part of these Terms, those laws will take precedence to the extent
            of the inconsistency.
          </p>
          <p className="medium">
            These Terms (along with the&nbsp;
            <a href="https://linktr.ee/s/privacy/">Privacy Notice</a>&nbsp;and
            any other policies we link to) make up the entire agreement between
            you and us in connection with the Platform. No other term is to be
            included, except where required by law. All implied terms except
            those implied by statute which cannot be excluded are expressly
            excluded.
          </p>
          <p className="medium">
            If any provision of these Terms is invalid under the law of any
            country, then it will be severed from these Terms in that country
            and the remainder of these Terms will continue to apply.
          </p>
          <p className="medium">
            These Terms are governed by the laws of Victoria and the
            Commonwealth of Australia. Both you and we submit to the exclusive
            jurisdiction of the courts of those jurisdictions.
          </p>
          <p className="medium">
            Our failure to insist upon or enforce any provision of these Terms
            will not be considered a waiver of any provision or right. Neither
            these Terms, nor the Platform, create a relationship of agent /
            principal between you and us.
          </p>
          <p>
            By using CelebriLinks’s Music Link or Video link functions to
            display Youtube videos on your CelebriLinks you are agreeing to be
            bound by the&nbsp;
            <a
              className="c-link"
              href="https://www.youtube.com/static?template=terms&amp;gl=AU"
              target="_blank"
            >
              YouTube Terms of Service.
            </a>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
